import React, {useContext, useEffect, useRef, useState} from "react";
import {Form} from "react-bootstrap";
import {useForm} from "react-hook-form";
import {makeGetRequest, makePatchRequest, makePostRequest} from "Services/ServicesUtils";
import {API_AUDITS} from "Models/Audit/AuditEndpoints";
import {insertNotification} from "Utils/NotificationsUtils";
import {GSPSTextArea} from "Components/Common/GSPSTextAreaInput";
import {UserContext} from "App";
import {useNavigate} from "react-router-dom";
import PropTypes from "prop-types";
import {Button} from "@mui/material";
import {InfoForm} from "./InfoForm.styles";
import {ContactsAutoComplete} from "Components/Contacts/ContactsAutoComplete";
import {SVPSubmitButton} from "Components/Buttons/SVPSubmitButton";
import {AuditStatusID} from "Models/Audit/AuditConstants";

export const ReviewerInformation = ({
    audit,
    users,
    readonly,
    isPublicRecord,
}) => {
    const {handleSubmit, register, setValue, formState: {errors}} = useForm();
    const {user} = useContext(UserContext) || {};
    const [selectedReviewer, setSelectedReviewer] = useState();
    const [reviewerType, setReviewerType] = useState("submit");
    const navigate = useNavigate();
    const endpointToUseAudit = isPublicRecord ? API_AUDITS.publicAudits : API_AUDITS.audits;
    const [inReview, setInReview] = useState(false);
    const autosaveNotesTimeout = useRef(null);
    const [isCompleteAudit, setIsCompleteAudit] = useState(false);

    useEffect(() => {
        let reviewer = null;
        if (audit) {
            makeGetRequest(`${endpointToUseAudit}${audit.id}/`).then((res) => {
                setInReview(Number(audit.audit_status.id) === AuditStatusID.IN_REVIEW);

                reviewer = res.reviewer || user;
                setSelectedReviewer(reviewer);

                if (reviewer) {
                    setValue("reviewer_id", reviewer.id, {shouldValidate: true});
                    setValue("phone", reviewer.mobile_phone);
                    setValue("email", reviewer.email);
                }
            }).catch((e) => insertNotification("Error", `Can't get audit ${audit.id} details`, "error"));
        } else {
            reviewer = user;
            setSelectedReviewer(reviewer);
            setValue("reviewer_id", reviewer.id, {shouldValidate: true});
            setValue("phone", reviewer.mobile_phone);
            setValue("email", reviewer.email);
        }
    }, [audit, user, setValue]);

    useEffect(() => {
        if (selectedReviewer) {
            setValue("reviewer_id", selectedReviewer.id, {shouldValidate: true});
            setValue("phone", selectedReviewer.mobile_phone);
            setValue("email", selectedReviewer.email);
        }
    }, [selectedReviewer]);

    const onSubmit = async (values) => {
        values.submission_type = reviewerType;
        if (inReview) {
            setIsCompleteAudit(true);
            try {
                makePostRequest(`${endpointToUseAudit}${audit.id}/reviewer/`, values)
                    .then((res) => {
                        if (values.submission_type === "submit") {
                            insertNotification("Success", `${audit.name} completed successfully ..`, "success");
                        } else {
                            insertNotification("Success", `${audit.name}
                            is set to edits required successfully ..`, "success");
                        }
                        navigate("/");
                    });
            } catch (e) {
                if (e instanceof TypeError) {
                    insertNotification("Error", "Can't set Inspection reviewer, please save the Inspection first.",
                        "error");
                } else {
                    insertNotification("Error", `Can't set Inspection reviewer ${e}`, "error");
                }
            } finally {
                setIsCompleteAudit(false);
            }
        } else {
            insertNotification("Error", "Audit must be in 'In Review' status to perform this action.", "error");
        }
    };
    const updateNotes = async (notes) => {
        clearTimeout(autosaveNotesTimeout.current);
        autosaveNotesTimeout.current = setTimeout(async () => {
            if (audit && notes !== audit?.reviewer_additional_notes && !readonly) {
                const updatedAudit = {
                    reviewer_additional_notes: notes,
                };
                makePatchRequest(`${API_AUDITS.audits}${audit.id}/`, updatedAudit);
            }
        }, 1000);
    };

    return (
        <InfoForm>
            <div className="container" id="reviewer">
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <h1 className="subtitle-md">
                        Review Information
                    </h1>
                    <div className="row">
                        <div className="col-md-8 col-12 ps-4 d-flex flex-column justify-content-between">
                            {selectedReviewer &&
                                <div>
                                    <div className="row">
                                        <h2 className="col-md-5 col-12 body-md d-flex align-items-center">
                                            Reviewer
                                        </h2>
                                        <div className="col mt-4">
                                            <ContactsAutoComplete
                                                users={users}
                                                setContact={setSelectedReviewer}
                                                contact={selectedReviewer}
                                                isDisabled={readonly}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                        <div className="col-md-4 col-12">
                            <h2 className="body-md mb-2">
                                Additional Notes
                            </h2>
                            <GSPSTextArea
                                value={audit?.reviewer_additional_notes}
                                labelName={"Additional Notes"}
                                id={"additional_notes"}
                                controlType={"input"}
                                isRequired={false}
                                isDisabled={readonly}
                                register={register}
                                rows={7}
                                errors={errors}
                                setValue={setValue}
                                charLimit={700}
                                hiddenLabel={true}
                                hiddenCharLimit={true}
                                setDescription={() => {}}
                                autoSave={true}
                                updateOnChange={updateNotes}
                            />
                        </div>
                        {
                            !readonly && <div className="float-end">
                                <SVPSubmitButton
                                    isLoading={!isCompleteAudit}
                                    className="float-end mt-3 cta-btn"
                                    buttonLoadingText="Completing..."
                                    buttonText="Complete Inspection"
                                    onClick={() => setReviewerType("submit")}
                                />
                                <Button
                                    type="submit"
                                    variant="outlined"
                                    onClick={() => setReviewerType("requestEdits")}
                                    className="float-end me-3 mt-3 cta-btn-secondary"
                                >
                                    Request Edits
                                </Button>
                            </div>
                        }
                    </div>
                </Form>
            </div>
        </InfoForm>);
};

ReviewerInformation.propTypes = {
    audit: PropTypes.object,
    users: PropTypes.array,
    readonly: PropTypes.bool,
    isPublicRecord: PropTypes.bool,
};
